import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { ArrowRight, Loader2 } from 'lucide-react'
import { StatusBadge } from '@/components/status-badge'
import { WebVitals } from '@/components/web-vitals'
import { PreviewScreenshot } from '@/components/preview-screenshot'
import { apiV2 } from '@/lib/api-v2-client'
import { describeBrowserFailure } from '@/lib/browser-check-reason'

export const Route = createFileRoute('/_site/tools/browser-check')({
  component: BrowserCheckToolPage,
  head: () => ({
    meta: [
      { title: 'Free Browser Check — Screenshot + Core Web Vitals — Uptrack' },
      {
        name: 'description',
        content:
          'Load any URL in a real browser and see a screenshot plus Core Web Vitals (LCP, CLS, FCP, TTFB). Free, no signup.',
      },
      { property: 'og:title', content: 'Free Browser Check — Screenshot + Web Vitals' },
      {
        property: 'og:description',
        content: 'See your site the way users do: a real-browser screenshot + Core Web Vitals, free and instant.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/tools-browser-check.png',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/tools/browser-check' }],
  }),
})

const EXAMPLES = ['https://wikipedia.org', 'https://playwright.dev', 'https://github.com']

function BrowserCheckToolPage() {
  const [url, setUrl] = useState('')
  const [ranUrl, setRanUrl] = useState('')
  const preview = useMutation({
    mutationFn: (u: string) => apiV2.browserPreview(u),
  })

  const run = (u: string) => {
    const target = u.trim()
    if (!target) return
    setUrl(target)
    const normalized = target.includes('://') ? target : `https://${target}`
    setRanUrl(normalized)
    preview.mutate(normalized)
  }

  // Hand the just-checked URL to the new-monitor form through signup. localStorage
  // survives the auth round-trip (incl. OAuth); the form reads + clears it on mount.
  // type: 'browser' so the user lands on the same kind of check they just previewed.
  const startMonitoring = () => {
    try {
      localStorage.setItem('uptrack_prefill', JSON.stringify({ url: ranUrl, type: 'browser' }))
    } catch {
      /* localStorage unavailable — signup still works, just without the prefill */
    }
  }

  let host = ranUrl
  try {
    if (ranUrl) host = new URL(ranUrl).host
  } catch {
    host = ranUrl
  }

  return (
    <>
      <>
        <section className="container mx-auto max-w-4xl px-4 py-12 md:py-16">
          <div className="mb-8 max-w-3xl">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Free browser check</h1>
            <p className="mt-4 max-w-2xl text-lead text-body">
              See your site the way a user does — a real headless-browser screenshot plus Core Web Vitals.
              Free, instant, no signup.
            </p>
          </div>

          <form
            className="flex flex-col gap-3 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault()
              run(url)
            }}
          >
            <Input
              aria-label="URL to check"
              className="h-12 text-lg"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoFocus
            />
            <Button type="submit" size="lg" disabled={preview.isPending}>
              {preview.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Run check'}
            </Button>
          </form>

          <p className="mt-2 text-sm text-muted-foreground">
            Try:{' '}
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                type="button"
                className="mr-1 rounded bg-secondary px-2 py-0.5 font-mono text-xs hover:bg-muted"
                onClick={() => run(ex)}
              >
                {ex.replace('https://', '')}
              </button>
            ))}
          </p>

          {preview.isPending && (
            <p className="mt-6 text-sm text-muted-foreground">
              Loading the page in a real browser — this takes ~10–20 seconds…
            </p>
          )}

          {preview.isError && (
            <div className="mt-6 rounded-lg border border-status-down bg-status-down-soft px-4 py-3 text-sm text-status-down">
              {preview.error instanceof Error ? preview.error.message : 'Check failed. Try again.'}
            </div>
          )}

          {preview.data && (
            <Card className="mt-8 border-border bg-card">
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-center gap-2">
                  <StatusBadge status={preview.data.status === 'up' ? 'up' : 'down'} />
                  <span className="text-sm text-muted-foreground">
                    {preview.data.status === 'up' ? 'Loaded in' : 'Failed after'} {preview.data.duration_ms.toLocaleString()} ms
                  </span>
                </div>
                {preview.data.status !== 'up' && <DownReason raw={preview.data.error} />}
                {preview.data.screenshot_url && (
                  <PreviewScreenshot url={preview.data.screenshot_url} />
                )}
                {preview.data.web_vitals && <WebVitals vitals={preview.data.web_vitals} />}
                <div className="rounded-lg border border-border bg-background p-4">
                  <p className="font-medium">Monitor {host || 'this page'} on a schedule</p>
                  <p className="mt-1 text-sm text-body">
                    Get this screenshot + Core Web Vitals on every run, with visual-change and
                    Web Vitals alerts. Pro checks it every 10 minutes; Business checks it every
                    minute.
                  </p>
                  <Button asChild className="mt-3">
                    <Link
                      to="/signup"
                      search={{ redirect: '/dashboard/monitors/new' }}
                      onClick={startMonitoring}
                    >
                      Monitor {host || 'this page'} <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </section>
      </>
      </>
  )
}

/** Why the page is Down, in plain words. The reason stays on screen: the user needs time to read it. */
function DownReason({ raw }: { raw: string | undefined }) {
  const { summary, detail } = describeBrowserFailure(raw)
  return (
    <div role="status" data-testid="down-reason" className="space-y-1 text-sm">
      <p className="text-body">{summary}</p>
      {detail && <p className="break-all font-mono text-xs text-muted-foreground">{detail}</p>}
    </div>
  )
}

